import LeaderboardTable, {
  LeaderboardColumn,
} from '#/components/Leaderboard/shared/leaderboard-table/LeaderboardTable';
import { useLiquidityResponsesState } from '@paradigm/features/src/leaderboard/contexts/liquidity-responses-list';
import { LiquidityResponse } from '@paradigm/features/src/leaderboard/entities/liquidity';
import {
  HeaderCell,
  RankCell,
  FirmCell,
  PercentageCell,
  BigNumberCell,
  Cell,
  EmptyCell,
} from '#/components/Leaderboard/shared/leaderboard-table/cells';
import { bigNumberSortBy } from '#/components/Leaderboard/shared/leaderboard-table/sort';

const columns = getColumns();
export default function LiquidityResponsesTable() {
  const { liquidityResponses, status } = useLiquidityResponsesState();

  return (
    <LeaderboardTable<LiquidityResponse>
      data={liquidityResponses}
      status={status}
      columns={columns}
      data-testid="leaderboard-liquidity-responses-table"
      tableRowAlign="right"
    />
  );
}

function getColumns(): LeaderboardColumn<LiquidityResponse>[] {
  return [
    {
      id: 'rank',
      header: ({ column }) => <HeaderCell column={column} title="Rank" />,
      cell: ({ cell }) => <RankCell isUserRow={cell.row.original.isFromUser} />,
      size: 70,
      enableResizing: false,
      enableSorting: false,
      meta: { isMobileHeader: true, draggable: false },
    },
    {
      id: 'firm',
      accessorKey: 'firmName',
      header: ({ column }) => <HeaderCell column={column} title="Firm" />,
      size: undefined,
      cell: (props) => <FirmCell {...props} isLiquidityTab />,
      enableResizing: false,
      enableSorting: false,
      meta: { isMobileHeader: true, draggable: false },
    },
    {
      id: 'spacer',
      accessorKey: 'spacer',
      header: EmptyCell,
      cell: EmptyCell,
      enableResizing: false,
      enableSorting: false,
      meta: { isMobileHeader: true, draggable: false },
    },
    {
      id: 'total_received',
      header: ({ column }) => (
        <HeaderCell column={column} title="RFQs Received" />
      ),
      accessorKey: 'totalReceived',
      enableResizing: false,
      cell: BigNumberCell,
      size: 150,
      sortingFn: bigNumberSortBy('totalReceived'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      sortingDirection: 'desc',
    },
    {
      id: 'quote_rate',
      header: ({ column }) => <HeaderCell column={column} title="Quote Rate" />,
      accessorKey: 'quoteRate',
      enableResizing: false,
      size: 120,
      cell: PercentageCell,
      sortingFn: bigNumberSortBy('quoteRate'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      sortingDirection: 'desc',
    },
    {
      id: 'median_response_time',
      header: ({ column }) => (
        <HeaderCell column={column} title="Median Response" />
      ),
      accessorKey: 'medianResponseTime',
      enableResizing: false,
      size: 160,
      cell: ({ cell }) => (
        <Cell
          data-testid={cell.column.id}
          highlight={cell.column.getIsSorted() !== false}
        >
          {cell.getValue<bigint>().toString()}s
        </Cell>
      ),
      sortingFn: bigNumberSortBy('medianResponseTime'),
      meta: { isMobileHeader: true, draggable: false },
      sortingDirection: 'asc',
    },
    {
      id: 'response_time_under_5s_rate',
      header: ({ column }) => <HeaderCell column={column} title="Under 5s" />,
      accessorKey: 'under5sResponse',
      enableResizing: false,
      size: 120,
      cell: PercentageCell,
      sortingFn: bigNumberSortBy('under5sResponse'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      isDefaultSorting: true,
      sortingDirection: 'desc',
    },
    {
      id: 'response_time_under_20s_rate',
      header: ({ column }) => <HeaderCell column={column} title="Under 20s" />,
      accessorKey: 'under20sResponse',
      enableResizing: false,
      size: 120,
      cell: PercentageCell,
      sortingFn: bigNumberSortBy('under20sResponse'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      sortingDirection: 'desc',
    },
    {
      id: 'response_time_under_60s_rate',
      header: ({ column }) => <HeaderCell column={column} title="Under 60s" />,
      accessorKey: 'under60sResponse',
      enableResizing: false,
      size: 120,
      cell: PercentageCell,
      sortingFn: bigNumberSortBy('under60sResponse'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      sortingDirection: 'desc',
    },
    {
      id: 'response_time_above_60s_rate',
      header: ({ column }) => <HeaderCell column={column} title="Above 60s" />,
      accessorKey: 'over60sResponse',
      enableResizing: false,
      size: 120,
      cell: PercentageCell,
      sortingFn: bigNumberSortBy('over60sResponse'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      sortingDirection: 'desc',
    },
  ];
}
